import { type CSSProperties } from 'react';

import { union, uniq } from 'lodash-es';

import { type Theme } from '@mui/material/styles';

import { extractKeys, pruneObject } from '@craft/lib/primitives';

import { exhaustiveBorderStyle } from '../borders';
import { type Color, evaluateHtmlColor } from '../colors';

import * as constants from './constants';
import * as types from './types';

/**
 * Filters a partial {@link types.InteractionStyle} to only include properties associated with
 * the provided indicator(s). Returns an empty object when `indicator` is undefined. When
 * `indicator` is defined, a property is included only if its associated indicator matches;
 * its value is taken from `overrides` when present, or falls back to the interaction default.
 *
 * @param {Partial<types.InteractionStyle>} overrides Explicit style overrides to apply.
 * @param {types.Interaction} interaction The interaction used to resolve default values.
 * @param {types.InteractionIndicator | types.InteractionIndicator[] | undefined} indicator
 *   The indicator(s) to filter by. When undefined, no properties are included.
 */
export const parseIndicatorStyle = (
  overrides: Partial<types.InteractionStyle>,
  interaction: types.Interaction,
  indicator: types.InteractionIndicator | types.InteractionIndicator[] | undefined,
): Partial<types.InteractionStyle<null>> => {
  if (indicator === undefined) {
    return {};
  }
  let style: Partial<types.InteractionStyle<null>> = {};
  for (const key of types.InteractionStyleProperties) {
    if (!constants.isInteractionStylePropertyAssociatedWithIndicator(key, indicator)) {
      continue;
    }
    const value = overrides[key];
    if (value === undefined || value === types.DefaultSxProperty) {
      style = {
        ...style,
        [key]: constants.getDefaultInteractionStyle(interaction, { property: key }),
      };
    } else if (value === null) {
      style = { ...style, [key]: null };
    } else {
      style = { ...style, [key]: value };
    }
  }
  return pruneObject(style);
};

/**
 * Merges two {@link types.InteractionStyle} objects into one. When both contain a value for the
 * same key, `b` takes precedence. Keys present in only one object are preserved as-is.
 */
const mergeStyle = <
  Arg extends
    | { [key in keyof CSSProperties]: CSSProperties[key] }
    | Partial<types.InteractionStyle<null>>,
>(
  a: Arg,
  b: Arg,
) => {
  let merged: Arg = {} as Arg;
  const keys = union(extractKeys(a), extractKeys(b));
  for (const key of keys) {
    if (b[key] === null) {
      merged = { ...merged, [key]: null };
    } else {
      merged = { ...merged, [key]: b[key] ?? a[key] };
    }
  }
  return pruneObject(merged);
};

const mergeSettingIntoOverrides = (
  overrides: Partial<types.InteractionStyle<null>>,
  setting: types.Interaction | types.InteractionSetting,
  interaction: types.Interaction,
): Partial<types.InteractionStyle<null>> => {
  if (!types.isInteractionSettingApplicableForInteraction(setting, interaction)) {
    return overrides;
  }
  if (types.isInteraction(setting)) {
    return mergeStyle(
      overrides,
      parseIndicatorStyle({}, interaction, constants.getDefaultInteractionIndicators(interaction)),
    );
  }
  return mergeStyle(
    overrides,
    parseIndicatorStyle(setting.overrides ?? {}, interaction, setting.indicator),
  );
};

export const parseInteractionStyle = (
  theme: Theme,
  style: Partial<types.InteractionStyle<null>>,
  interaction: types.Interaction,
): CSSProperties => {
  const unwrapColor = (colorDef: ((t: Theme) => string) | Color) =>
    typeof colorDef === 'function' ? colorDef(theme) : colorDef;

  return pruneObject({
    ...(style.borderColor
      ? exhaustiveBorderStyle(theme, {
          color: unwrapColor(style.borderColor),
          thickness:
            style.borderWidth ?? constants.getDefaultInteractionStyle(interaction).borderWidth,
          useOutline: style.useOutline ?? true,
        })
      : undefined),
    backgroundColor: style.backgroundColor
      ? evaluateHtmlColor(theme, unwrapColor(style.backgroundColor))
      : undefined,
  });
};

/**
 * Computes the resolved {@link CSSProperties} and CSS selectors for a single
 * {@link types.Interaction}, given an {@link types.InteractionSettings} array. Settings
 * applicable to the given interaction are merged in order, with later entries taking precedence.
 * If a setting provides a custom `selector`, that selector replaces the default for the
 * interaction. Returns `[{}, null]` when `settings` is null or undefined.
 *
 * @param {Theme} theme The MUI theme used to resolve color values.
 * @param {types.InteractionSettings} settings The interaction settings to evaluate.
 * @param {types.Interaction} interaction The specific interaction to resolve styles for.
 * @param {Pick<InteractableStyleOptions, 'selectors'>} [options] Optional selector overrides.
 */
export const interactionStyle = (
  theme: Theme,
  settings: types.InteractionSettings,
  interaction: types.Interaction,
  options?: Pick<InteractableStyleOptions, 'selectors'>,
): [CSSProperties, types.InteractableSelector[]] | [Record<never, never>, null] => {
  if (!settings) {
    return [{}, null];
  }
  const selectors: types.InteractableSelector[] = types.interactionSelectors(
    interaction,
    options?.selectors,
  );

  let accumulatedStyle: CSSProperties = {};
  let accumulatedOverrides: Partial<types.InteractionStyle<null>> = {};

  for (const setting of settings) {
    if (!setting || !types.isInteractionSettingApplicableForInteraction(setting, interaction)) {
      continue;
    }
    accumulatedOverrides = mergeSettingIntoOverrides(accumulatedOverrides, setting, interaction);
    if (!types.isInteraction(setting)) {
      accumulatedStyle = { ...accumulatedStyle, ...(setting.style ?? {}) };
    }
  }
  const style = mergeStyle(
    parseInteractionStyle(theme, accumulatedOverrides, interaction),
    accumulatedStyle,
  );
  return [style, uniq(selectors)];
};

/**
 * The result shape returned by {@link interactableStyle} when `options.as` is `'indexed'`. Maps
 * each {@link types.Interaction} that has resolved styles to its {@link CSSProperties} and the
 * list of selectors those styles apply to.
 */
type IndexedParsedInteractableStyle = Partial<
  Record<
    types.Interaction,
    {
      readonly selectors: types.InteractableSelector[];
      readonly style: CSSProperties;
    }
  >
>;

type ParsedInteractableCss = Partial<Record<types.InteractableSelector, CSSProperties>>;

type ParsedInteractableStyleAs = 'css' | 'indexed';

/**
 * Options accepted by {@link interactableStyle}.
 */
interface InteractableStyleOptions {
  /**
   * Controls the shape of the return value. When `'indexed'`, returns an
   * {@link IndexedParsedInteractableStyle} keyed by interaction. When `'css'` (the default),
   * returns a flat record keyed by CSS selector string.
   *
   * @default 'css'
   */
  readonly as?: ParsedInteractableStyleAs;
  /**
   * Optional map of custom CSS selectors that override the defaults for each
   * {@link types.Interaction}. See {@link types.InteractableSelectorMap}.
   */
  readonly selectors?: types.InteractableSelectorMap;
}

type ParsedInteractableStyle<O extends InteractableStyleOptions> = O extends {
  readonly as: 'indexed';
}
  ? IndexedParsedInteractableStyle
  : ParsedInteractableCss;

/**
 * Resolves all interaction styles from an {@link types.InteractionSettings} array into a single
 * CSS object. Iterates over all {@link types.Interaction} values, computes each via
 * {@link interactionStyle}, and combines results keyed by their CSS selector(s).
 *
 * The return type depends on `options.as`:
 * - `'css'` (default): a flat `Partial<Record<InteractableSelector, CSSProperties>>`.
 * - `'indexed'`: an {@link IndexedParsedInteractableStyle} keyed by interaction.
 *
 * @param {Theme} theme The MUI theme used to resolve color values.
 * @param {types.InteractionSettings | undefined} settings
 *   The interaction settings to evaluate, or undefined for an empty result.
 * @param {O} [options] Controls the return shape and selector overrides.
 */
export const interactableStyle = <O extends InteractableStyleOptions>(
  theme: Theme,
  settings: types.InteractionSettings | undefined,
  options?: O,
): ParsedInteractableStyle<O> => {
  let parsed: {
    readonly indexed: IndexedParsedInteractableStyle;
    readonly style: ParsedInteractableCss;
  } = { indexed: {}, style: {} };

  if (settings) {
    for (const interaction of Object.values(types.Interaction)) {
      const [style, selectors] = interactionStyle(theme, settings, interaction, options);
      if (Object.keys(style).length === 0 || selectors === null) {
        continue;
      }
      parsed = {
        ...parsed,
        indexed: { ...parsed.indexed, [interaction]: { selectors, style } },
        style: {
          ...parsed.style,
          ...selectors.reduce(
            (acc: ParsedInteractableCss, selector) => ({ ...acc, [selector]: style }),
            {},
          ),
        },
      };
    }
  }

  if (options?.as === 'indexed') {
    const result: ParsedInteractableStyle<{ readonly as: 'indexed' }> = parsed.indexed;
    return result;
  }
  const result: ParsedInteractableStyle<{ readonly as: 'css' }> = parsed.style;
  return result;
};
